import { countries } from './countriesList';

export type AccountCurrency =
    | 'USD'
    | 'NGN'
    | 'EUR'
    | 'GBP'
    | 'GHS'
    | 'KES'
    | 'ZAR'
    | 'UGX'
    | 'TZS'
    | 'RWF'
    | 'ZMW'
    | 'MWK'
    | 'EGP'
    | 'XOF'
    | 'XAF';

const EUR_COUNTRY_CODES = new Set([
    'AT',
    'BE',
    'BG',
    'CY',
    'DE',
    'EE',
    'ES',
    'FI',
    'FR',
    'GR',
    'HR',
    'IE',
    'IT',
    'LT',
    'LU',
    'LV',
    'MT',
    'NL',
    'PT',
    'RO',
    'SI',
    'SK',
    'AD',
    'MC',
    'SM',
    'VA',
]);

const FLUTTERWAVE_CURRENCY_BY_COUNTRY: Record<string, AccountCurrency> = {
    UG: 'UGX',
    TZ: 'TZS',
    RW: 'RWF',
    ZM: 'ZMW',
    MW: 'MWK',
    EG: 'EGP',
    BJ: 'XOF',
    BF: 'XOF',
    CI: 'XOF',
    GW: 'XOF',
    ML: 'XOF',
    NE: 'XOF',
    SN: 'XOF',
    TG: 'XOF',
    CM: 'XAF',
    CF: 'XAF',
    TD: 'XAF',
    CG: 'XAF',
    GQ: 'XAF',
    GA: 'XAF',
};

/**
 * Default settlement currency for new external accounts, aligned with country.
 */
export function defaultAccountCurrencyForCountry(countryCode: string | undefined): AccountCurrency {
    if (!countryCode) return 'USD';
    const c = countryCode.toUpperCase();
    if (c === 'NG') return 'NGN';
    if (c === 'GH') return 'GHS';
    if (c === 'KE') return 'KES';
    if (c === 'ZA') return 'ZAR';
    if (c === 'GB') return 'GBP';
    if (EUR_COUNTRY_CODES.has(c)) return 'EUR';
    if (FLUTTERWAVE_CURRENCY_BY_COUNTRY[c]) return FLUTTERWAVE_CURRENCY_BY_COUNTRY[c];
    return 'USD';
}

/** Resolve ISO country code to a readable label (original list stored `country` as code, e.g. NG). */
export function displayCountryLabel(countryCode: string | undefined): string {
    if (!countryCode) return '—';
    const row = countries.find(x => x.code === countryCode.toUpperCase());
    return row?.name ?? countryCode;
}

const VALID_CURRENCY = new Set<string>([
    'USD',
    'NGN',
    'EUR',
    'GBP',
    'GHS',
    'KES',
    'ZAR',
    'UGX',
    'TZS',
    'RWF',
    'ZMW',
    'MWK',
    'EGP',
    'XOF',
    'XAF',
]);

/**
 * Currency shown on account cards: trust API when valid, fix known bad combos,
 * normalize odd values, fall back to country default.
 */
export function displayAccountCurrency(account: { account_currency?: string; country?: string }): AccountCurrency {
    const raw = String(account.account_currency ?? '')
        .trim()
        .toUpperCase();
    const country = account.country?.toUpperCase() ?? '';

    if (raw === 'NG') return 'NGN';

    if (VALID_CURRENCY.has(raw)) {
        // Modal used to send USD for every country; Nigeria accounts should read as NGN.
        if (country === 'NG' && raw === 'USD') {
            return 'NGN';
        }
        return raw as AccountCurrency;
    }

    if (!raw) {
        return defaultAccountCurrencyForCountry(country);
    }

    return defaultAccountCurrencyForCountry(country);
}
